import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { getKioskDeviceToken } from './lib/kioskToken';
import KioskScreen from './kiosk/KioskScreen';
import LandingRoute from './LandingRoute';
import { lazyPage } from './lazyPage';
import Nachgeladen from './Nachgeladen';

// 2026-10-11: Nur Kiosk und Startseite gehören zum ersten Paket. Alles andere wird nachgeladen
// (lazyPage.ts) -- vor allem, damit Kiosk-Tablet und Handy nicht den ganzen Admin-Bereich samt
// MUI herunterladen. Das Vereins-Theme kommt mit `ThemedOutlet`, ebenfalls nachgeladen.
const ThemedOutlet = lazyPage(() => import('./ThemedOutlet'));
const MemberScreen = lazyPage(() => import('./member/MemberScreen'));
const LoginPage = lazyPage(() => import('./admin/LoginPage'));
const ResetPasswordPage = lazyPage(() => import('./admin/ResetPasswordPage'));
const AdminLayout = lazyPage(() => import('./admin/AdminLayout'));
const AdminHome = lazyPage(() => import('./admin/AdminHome'));
const DivisionsPage = lazyPage(() => import('./admin/DivisionsPage'));
const MembersPage = lazyPage(() => import('./admin/MembersPage'));
const BeveragesPage = lazyPage(() => import('./admin/BeveragesPage'));
const ChairsPage = lazyPage(() => import('./admin/ChairsPage'));
const AdminsPage = lazyPage(() => import('./admin/AdminsPage'));
const EventsPage = lazyPage(() => import('./admin/EventsPage'));
const EventDetailPage = lazyPage(() => import('./admin/EventDetailPage'));
const KioskSetupPage = lazyPage(() => import('./admin/KioskSetupPage'));
const KioskAccountsPage = lazyPage(() => import('./admin/KioskAccountsPage'));
const CardReadersPage = lazyPage(() => import('./admin/CardReadersPage'));
const PerMemberEventReportPage = lazyPage(() => import('./admin/PerMemberEventReportPage'));
const PerDivisionMonthReportPage = lazyPage(() => import('./admin/PerDivisionMonthReportPage'));
const PurchasePricesPage = lazyPage(() => import('./admin/PurchasePricesPage'));
const SettlementPage = lazyPage(() => import('./admin/SettlementPage'));
const BelegePage = lazyPage(() => import('./admin/BelegePage'));
const BookingsPage = lazyPage(() => import('./admin/BookingsPage'));
const HistoricalPurgePage = lazyPage(() => import('./admin/HistoricalPurgePage'));
const HandbuchAbteilungsleiterPage = lazyPage(() => import('./admin/HandbuchAbteilungsleiterPage'));
const HandbuchAdminPage = lazyPage(() => import('./admin/HandbuchAdminPage'));
const NeuigkeitenPage = lazyPage(() => import('./admin/NeuigkeitenPage'));
const OeffentlicheNeuigkeitenPage = lazyPage(() => import('./admin/OeffentlicheNeuigkeitenPage'));
const InventoryPage = lazyPage(() => import('./admin/InventoryPage'));
const DeliveriesPage = lazyPage(() => import('./admin/DeliveriesPage'));

/**
 * Eine nachgeladene Seite. Die Ladeanzeige sitzt INNERHALB des Layouts: Beim Wechsel zwischen
 * Admin-Seiten bleibt die Seitenleiste stehen, nur der Inhalt zeigt kurz „Lädt…".
 */
const page = (el: React.ReactElement) => <Nachgeladen>{el}</Nachgeladen>;

// 2026-10-11: Die gemeinsame Hülle (PublicShell) ist entfallen. Ihre letzte Seite war `/mitglied`;
// die setzt ihre Leiste jetzt selbst (member/MitgliedHuelle.tsx), weil die Code-Eingabe eine Karte
// mit eigenem Wappen ist. Das Wappen dort führt zur Handy-Seite, nicht zum Kiosk (27.08.).

/**
 * Containment guard for the kiosk (v0.10 follow-up to FR-180).
 *
 * v0.10 cleared the admin session at *bind* time, which closed the hole for a
 * device that was bound and then left alone. It does not survive the flow the
 * club reported on 2026-07-30: an admin logs back in on the tablet -- which
 * FR-184 deliberately allows, so the binding can be changed or removed -- and
 * from then on „Zum Kiosk" (or the logo, which points at /kiosk from every
 * page) returns to the kiosk with a live session, and Back walks straight into
 * the admin panel. Binding is simply not the only way onto the kiosk screen.
 *
 * So the invariant moves from the binding to the kiosk itself: whenever this
 * device shows the kiosk, it holds the device token and nothing else. That is
 * enforceable at exactly one place -- here -- and covers every route in,
 * including the address bar, the Back button, a bookmark and the logo.
 *
 * Gated on the device token so it stays narrow: an admin previewing /kiosk from
 * their own laptop has no token, nothing to contain, and signing them out would
 * strand them -- the unbound kiosk screen's only way onward is a link into
 * /admin/kiosk-setup, which needs a session.
 *
 * Deliberately mount-only, not a running `storage` watch: a bound device must
 * still let an admin log in and reach Kiosk-Einrichtung to unbind (FR-184), and
 * a kiosk tab that kept sweeping would fight that login in another tab.
 */
function KioskRoute() {
  const { logout } = useAuth();
  const bound = getKioskDeviceToken() !== undefined;

  useEffect(() => {
    if (bound) logout();
  }, [bound, logout]);

  return <KioskScreen />;
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { loading, idToken } = useAuth();
  if (loading) return <p>Lädt…</p>;
  if (!idToken) return <Navigate to="/admin/login" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* In a browser this still goes straight to the admin login (someone
            typing the club address by hand is almost always an admin), and
            the event tablet is still bookmarked directly at /kiosk. From an
            INSTALLED icon it has to decide differently -- see LandingRoute. */}
        <Route path="/" element={<LandingRoute />} />
        {/* 2026-09-25: Der Kiosk hängt NICHT mehr in der gemeinsamen Hülle -- er bringt seine
            eigene obere Leiste mit, weil dort jetzt der Eventname und der Modus stehen (Owner:
            „die Information könnten wir in den Bereich ganz oben bringen"). Das ist die einzige
            Stelle, an der diese Angaben nichts an Höhe kosten, und auf dem Tablet zählt jede
            Zeile. Die Containment-Sperre (v0.10) sitzt unverändert in KioskRoute. */}
        <Route path="/kiosk" element={<KioskRoute />} />
        {/* Alles darunter benutzt MUI; das Theme wird mit der ersten dieser Seiten nachgeladen. */}
        <Route element={page(<ThemedOutlet />)}>
          {/* v0.15: the member's own screen, opened from the home-screen
              icon. Public shell (brand bar, no admin nav) and no auth guard --
              it identifies with its own code, not a Cognito login. */}
          <Route path="/mitglied" element={page(<MemberScreen />)} />
          {/* 2026-10-11: Anmeldung und Zurücksetzen ohne die obere Leiste -- eine Karte mit
              Wappen wie in MyHours (AuthCard). „Zum Kiosk" steht in der Karte (v0.10 AC-168). */}
          <Route path="/admin/login" element={page(<LoginPage />)} />
          {/* v0.5 FR-108: standalone, unauthenticated reset page reachable
              directly from the emailed reset link -- no old password needed. */}
          <Route path="/admin/reset" element={page(<ResetPasswordPage />)} />
          {/* 2026-10-21 (Owner): die Neuigkeiten ohne Anmeldung -- für den geteilten Link. */}
          <Route path="/neuigkeiten" element={page(<OeffentlicheNeuigkeitenPage />)} />
          <Route path="/admin" element={<RequireAuth>{page(<AdminLayout />)}</RequireAuth>}>
            <Route index element={page(<AdminHome />)} />
            <Route path="divisions" element={page(<DivisionsPage />)} />
            <Route path="members" element={page(<MembersPage />)} />
            <Route path="beverages" element={page(<BeveragesPage />)} />
            <Route path="chairs" element={page(<ChairsPage />)} />
            <Route path="admins" element={page(<AdminsPage />)} />
            <Route path="events" element={page(<EventsPage />)} />
            <Route path="events/:eventId" element={page(<EventDetailPage />)} />
            <Route path="kiosk-setup" element={page(<KioskSetupPage />)} />
            <Route path="kiosk-accounts" element={page(<KioskAccountsPage />)} />
            <Route path="card-readers" element={page(<CardReadersPage />)} />
            <Route path="reports/per-member-event" element={page(<PerMemberEventReportPage />)} />
            <Route
              path="reports/per-division-month"
              element={page(<PerDivisionMonthReportPage />)}
            />
            <Route path="purchase-prices" element={page(<PurchasePricesPage />)} />
            <Route path="settlements" element={page(<SettlementPage />)} />
            <Route path="settlements/belege" element={page(<BelegePage />)} />
            <Route path="bookings" element={page(<BookingsPage />)} />
            <Route path="inventory" element={page(<InventoryPage />)} />
            <Route path="deliveries" element={page(<DeliveriesPage />)} />
            <Route path="historical-purge" element={page(<HistoricalPurgePage />)} />
            <Route
              path="handbuch/abteilungsleiter"
              element={page(<HandbuchAbteilungsleiterPage />)}
            />
            <Route path="handbuch/admin" element={page(<HandbuchAdminPage />)} />
            <Route path="neuigkeiten" element={page(<NeuigkeitenPage />)} />
          </Route>
        </Route>
        {/* Unknown paths (typos, stale links) land on the login page too,
            rather than dropping someone into the kiosk unexpectedly. */}
        <Route path="*" element={<Navigate to="/admin/login" replace />} />
      </Routes>
    </AuthProvider>
  );
}
